import type { IntersectionDefinition, GroupDefinition, DetectorDefinition } from '../types/intersection';
import cabinetConfig from '../config/cabinet.json';
import groupsConfig from '../config/groups.json';
import detectorsConfig from '../config/detectors.json';

/**
 * Cruce Aragó × Passeig de Gràcia, Barcelona
 *
 * Coordenadas GPS reales de Open Data BCN (Inventario oficial de semáforos)
 * verificadas con Overpass API (OSM).
 *
 * Aragó: sentido mar (E) y montaña (W), 3 carriles por sentido
 * Pg. de Gràcia: sentido mar (S→N sube) y montaña (N→S baja), 3 carriles + carril bus
 *
 * Esquinas del cruce:
 *   SW: ~[2.1646, 41.3900]  NW: ~[2.1643, 41.3904]
 *   SE: ~[2.1659, 41.3912]  NE: ~[2.1658, 41.3914]
 *
 * Fases:
 *   A: Aragó verde (ambos sentidos), Pg. Gràcia rojo, peatones cruzando Pg. Gràcia
 *   B: Aragó ámbar, transición
 *   C: Pg. Gràcia verde (ambos sentidos), Aragó rojo, peatones cruzando Aragó
 *   D: Pg. Gràcia ámbar, transición
 *   E: Todo rojo (despeje), peatones rojo
 *   F: Verde peatonal parpadeante (fin paso peatones)
 */

const intersection: IntersectionDefinition = {
  id: 'arago-pg-gracia',
  name: 'Aragó × Passeig de Gràcia',
  center: [2.16510, 41.39075],
  zoom: 18.5,
  bearing: -45,
  pitch: 50,

  cabinet: cabinetConfig,

  groups: groupsConfig as unknown as GroupDefinition[],

  detectors: detectorsConfig as unknown as DetectorDefinition[],

  geometry: {
    // Carriles Aragó — la calle va de SW a NE (azimut ~65°)
    // Calzada sur: sentido mar (E/NE), calzada norte: sentido montaña (W/SW)
    lanes: [
      // Aragó sentido montaña (W) — calzada norte
      {
        type: 'Feature' as const,
        properties: { id: 'arago-w-1', direction: 'W' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16640, 41.39160],
            [2.16380, 41.38990]
          ]
        }
      },
      {
        type: 'Feature' as const,
        properties: { id: 'arago-w-2', direction: 'W' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16635, 41.39155],
            [2.16375, 41.38985]
          ]
        }
      },
      {
        type: 'Feature' as const,
        properties: { id: 'arago-w-3', direction: 'W' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16630, 41.39150],
            [2.16370, 41.38980]
          ]
        }
      },
      // Aragó sentido mar (E) — calzada sur
      {
        type: 'Feature' as const,
        properties: { id: 'arago-e-1', direction: 'E' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16360, 41.38965],
            [2.16620, 41.39135]
          ]
        }
      },
      {
        type: 'Feature' as const,
        properties: { id: 'arago-e-2', direction: 'E' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16355, 41.38960],
            [2.16615, 41.39130]
          ]
        }
      },
      {
        type: 'Feature' as const,
        properties: { id: 'arago-e-3', direction: 'E' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16350, 41.38955],
            [2.16610, 41.39125]
          ]
        }
      },
      // Pg. Gràcia bajada (S) — calzada este
      {
        type: 'Feature' as const,
        properties: { id: 'pgg-s-1', direction: 'S' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16610, 41.39180],
            [2.16430, 41.38950]
          ]
        }
      },
      {
        type: 'Feature' as const,
        properties: { id: 'pgg-s-2', direction: 'S' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16605, 41.39175],
            [2.16425, 41.38945]
          ]
        }
      },
      {
        type: 'Feature' as const,
        properties: { id: 'pgg-s-3', direction: 'S' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16600, 41.39170],
            [2.16420, 41.38940]
          ]
        }
      },
      // Pg. Gràcia subida (N) — calzada oeste
      {
        type: 'Feature' as const,
        properties: { id: 'pgg-n-1', direction: 'N' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16400, 41.38940],
            [2.16580, 41.39170]
          ]
        }
      },
      {
        type: 'Feature' as const,
        properties: { id: 'pgg-n-2', direction: 'N' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16405, 41.38945],
            [2.16585, 41.39175]
          ]
        }
      },
      {
        type: 'Feature' as const,
        properties: { id: 'pgg-n-3', direction: 'N' },
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [2.16410, 41.38950],
            [2.16590, 41.39180]
          ]
        }
      },
    ],

    // Pasos de peatones (4 lados del cruce)
    crosswalks: [
      // Paso norte-oeste (cruza Aragó, lado NW)
      {
        type: 'Feature' as const,
        properties: { id: 'cw-north', side: 'N' },
        geometry: {
          type: 'Polygon' as const,
          coordinates: [[
            [2.16415, 41.39035],
            [2.16460, 41.39060],
            [2.16455, 41.39065],
            [2.16410, 41.39040],
            [2.16415, 41.39035]
          ]]
        }
      },
      // Paso sur-oeste (cruza Aragó, lado SW)
      {
        type: 'Feature' as const,
        properties: { id: 'cw-south', side: 'S' },
        geometry: {
          type: 'Polygon' as const,
          coordinates: [[
            [2.16440, 41.38990],
            [2.16485, 41.39015],
            [2.16480, 41.39020],
            [2.16435, 41.38995],
            [2.16440, 41.38990]
          ]]
        }
      },
      // Paso norte-este (cruza Pg. Gràcia, lado NE)
      {
        type: 'Feature' as const,
        properties: { id: 'cw-east', side: 'E' },
        geometry: {
          type: 'Polygon' as const,
          coordinates: [[
            [2.16575, 41.39130],
            [2.16600, 41.39145],
            [2.16595, 41.39150],
            [2.16570, 41.39135],
            [2.16575, 41.39130]
          ]]
        }
      },
      // Paso sur-este (cruza Pg. Gràcia, lado SE)
      {
        type: 'Feature' as const,
        properties: { id: 'cw-west', side: 'W' },
        geometry: {
          type: 'Polygon' as const,
          coordinates: [[
            [2.16545, 41.39100],
            [2.16570, 41.39115],
            [2.16565, 41.39120],
            [2.16540, 41.39105],
            [2.16545, 41.39100]
          ]]
        }
      },
    ],

    // Área del cruce (polígono que cubre la intersección real)
    intersectionArea: {
      type: 'Feature' as const,
      properties: { id: 'intersection-area' },
      geometry: {
        type: 'Polygon' as const,
        coordinates: [[
          [2.16430, 41.38980],  // SW corner
          [2.16560, 41.39050],  // SE corner
          [2.16610, 41.39160],  // NE corner
          [2.16480, 41.39090],  // NW corner
          [2.16430, 41.38980]   // close
        ]]
      }
    }
  }
};

export default intersection;
